'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { YandexSuggestButton } from '@/components/auth/YandexSuggestButton';
import { buildOAuthRedirectUrl, fetchOAuthProviders, type OAuthProvider } from '@/lib/oauth';

interface OAuthButtonsProps {
  redirectPath?: string;
}

function SberMark() {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#21A038] text-[10px] font-bold text-white">
      С
    </span>
  );
}

export function OAuthButtons({ redirectPath = '/' }: OAuthButtonsProps) {
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const items = await fetchOAuthProviders();
        if (!cancelled) {
          setProviders(items);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading || providers.length === 0) {
    return null;
  }

  const yandex = providers.find((p) => p.id === 'yandex');
  const others = providers.filter((p) => p.id !== 'yandex');

  return (
    <div className="space-y-3">
      <div className="relative py-1">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-slate-200/80" />
        </div>
        <div className="relative flex justify-center text-[10px] font-semibold uppercase tracking-wider">
          <span className="bg-white/90 px-2 text-slate-400">или</span>
        </div>
      </div>

      <div className="grid gap-2">
        {yandex && (
          <YandexSuggestButton provider={yandex} redirectPath={redirectPath} />
        )}

        {others.map((provider) => (
          <Button
            key={provider.id}
            type="button"
            variant="outline"
            className="h-10 w-full justify-center gap-1.5 whitespace-nowrap rounded-xl border-[#21A038]/30 bg-white/70 font-semibold text-[#21A038] hover:bg-[#21A038]/5"
            onClick={() => {
              window.location.href = buildOAuthRedirectUrl(provider.id, redirectPath);
            }}
          >
            <SberMark />
            <span className="whitespace-nowrap">{provider.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
